<template>
  <div v-if="loading" class="text-center mt-5 font-2xl text-muted">
    <i class="fal fa-spinner fa-spin" /> Project Data Loading...
  </div>
  <div v-else class="animated fadeIn mb-5">
    <basic-heading>
      <span class="float-left mr-2">{{ project.publicNumber() }}</span>
      <badge-batch :badge_collection="project_badges" />
      <template #right-content>
        <header-button-group :button_config="button_config">
          <template #post_buttons>
            <button
              v-show="checkPermission('delete project')"
              title="Delete project"
              class="btn btn-danger ml-1"
              @click="showDeleteModal(project)"
            >
              <i class="fal fa-trash" />
            </button>
          </template>
        </header-button-group>
      </template>
    </basic-heading>
    <permission-alert-gate :permissions="'read project'">
      <template #gated-content>
        <status-progress
          :current_status="current_status"
          :log_collection="project_status_logs"
          status_group="project"
          class="mb-3"
        />
        <project-card :project="project" />
        <context-pills :items="project_items" class="mb-3" />
        <project-detail-cardstack :project="project" />
      </template>
    </permission-alert-gate>
  </div>
</template>

<script>
import Vue from "vue";
import VueNotifications from "vue-notifications";
import BasicHeading from "@/components/shared/BasicHeading";
import BadgeBatch from "@/components/shared/BadgeBatch";
import ContextPills from "@/components/shared/ContextPills";
import GeneralTile from "@/components/shared/GeneralTile";
import HeaderButtonGroup from "@/components/shared/headerbuttons/HeaderButtonGroup";
import LinkTile from "@/components/shared/LinkTile";
import PermissionAlertGate from "@/components/permission/PermissionAlertGate";
import ProjectCard from "@/components/lab/project/ProjectCard";
import ProjectDetailCardstack from "@/components/lab/project/ProjectDetailCardstack";
import StatusProgress from "@/components/shared/StatusProgress";
import { AccessControlCheck } from "@/auth/AccessControlCheck";
import { getModel } from "@/shared/fetch-utils";
import { LabRepositoryFactory } from "@/repositories/lab/LabRepositoryFactory";
import { notificationOptions } from "@/shared/toast-notification-options";
import { projectNotifications } from "@/shared/notifications";

Vue.use(VueNotifications, notificationOptions);

const ProjectRepository = LabRepositoryFactory.get("project");

export default {
  name: "ProjectDetail",
  components: {
    BasicHeading,
    BadgeBatch,
    ContextPills,
    GeneralTile,
    HeaderButtonGroup,
    LinkTile,
    PermissionAlertGate,
    ProjectCard,
    ProjectDetailCardstack,
    StatusProgress,
  },
  data() {
    return {
      loading: true,
      project_id: this.$route.params.project_id || null,
      project: null,
      project_items: null,
      project_status_logs: null,
      project_badges: null,
    };
  },
  computed: {
    button_config() {
      return {
        buttons: [
          {
            icon: "fa-list",
            linkto: "/projects",
            title: "Project list",
          },
          {
            premission: this.checkPermission("edit project"),
            icon: "fa-pencil",
            linkto: `/projects/${this.project_id}/edit`,
            title: "Edit project",
            variant: "warning",
          },
          {
            icon: "fa-barcode",
            linkto: `/samples/receive/${this.project_id}`,
            title: "Receive project samples",
            variant: "success",
          },
        ],
      };
    },
    current_status() {
      return (
        this.project &&
        this.project.current_project_status_log.project_status.status
      );
    },
  },
  methods: {
    checkPermission(permission) {
      return AccessControlCheck.checkPermission(permission);
    },
    async init() {
      this.project = await getModel({
        model_type: "project",
        id: this.project_id,
        message: this.$bvToast,
      });

      if (this.project) {
        this.initProject();

        this.loading = false;
      } else {
        this.$router.back();
      }
    },
    initProject() {
      this.project_status_logs = this.project.getProjectStatusLogs();
      this.project_items = [];
      this.project_badges = this.project.submissions.length
        ? this.project.getSubmissions().models.map((submission) => {
            return submission.getSampleType().name;
          })
        : [];
      const { customer, customer_contact, customer_location } = this.project;

      customer &&
        (this.project_items = [
          ...this.project_items,
          {
            to: `/customer/${customer.id}`,
            faIcon: this.project.getCustomer().icon(),
            variant: "dark",
            label: customer.full_name,
          },
        ]);
      customer_contact &&
        customer_contact.name &&
        (this.project_items = [
          ...this.project_items,
          {
            to: `/customer/${customer.id}/contacts/${customer_contact.id}/edit`,
            faIcon: this.project.getCustomerContact().icon(),
            variant: "dark",
            label: customer_contact.name,
          },
        ]);
      customer_location &&
        (this.project_items = [
          ...this.project_items,
          {
            to: `/customer/${customer.id}/locations/${customer_location.id}/edit`,
            faIcon: this.project.getCustomerLocation().icon(),
            variant: "dark",
            label: customer_location.name,
          },
        ]);

      this.project_items.push({
        to: `/projects/${this.project_id}/audit-trail`,
        faIcon: "search",
        variant: "dark",
        label: "Audit Trail",
      })
    },
    showDeleteModal() {
      this.$bvModal
        .msgBoxConfirm(
          "You are about to remove this project. Do you want to continue?",
          {
            title: "Are you sure?",
            modalClass: ["modal-danger"],
            size: "sm",
            okVariant: "danger",
            okTitle: "YES",
            cancelTitle: "NO",
            footerClass: "p-2",
            centered: true,
          }
        )
        .then(async (confirm) => {
          if (confirm) {
            try {
              await ProjectRepository.suspend(this.project_id);

              this.deleteSuccess();
              this.$router.push("/projects");
            } catch ({ response }) {
              const { status, data } = response.response;

              this.$bvToast.toast(`Code: ${status}. ${data.error}`, {
                title: "Server Error.",
                variant: "danger",
              });
            }
          }
        })
    }
  },
  created() {
    this.loading = true;

    this.init();
  },
  notifications: projectNotifications,
};
</script>
